import { Reducer } from 'interfaces'
import { VideoCallsAppState, CallPeer } from 'components/features/videoCalls/types/state'
import {
  AgoraLeaveChannelAction,
  AgoraClientEventAction,
  AgoraPublishLocalStream,
  AgoraScreenShareAction,
  AgoraStopScreenShareAction,
  AgoraMuteVideoAction,
  AgoraUnmuteVideoAction,
  AgoraMuteAudioAction,
  AgoraUnmuteAudioAction,
  AgoraUnPublishLocalStream,
} from 'components/features/videoCalls/types/actions'
import { createEmptyCollection, setItemInCollection, removeByKeyFromCollection, getItemFromCollection } from 'helpers/normalizeHelpers'
import { isScreenShare } from 'components/features/videoCalls/helpers'


const INITIAL_STATE = createEmptyCollection()
// const INITIAL_STATE = normalize(new Array(8).fill(0).map((_, index) => new CallPeer(index + 10_000_000, index + 10_000_000, true, true)), 'id')
export const peersReducer: Reducer<VideoCallsAppState['peers']> = (prevState = INITIAL_STATE, action) => {

  switch (action.type) {
    case AgoraScreenShareAction.type: {
      if (action.meta !== 'success') {
        return prevState
      }
      const { localStreamId } = action.payload.response
      return setItemInCollection(prevState, new CallPeer(localStreamId, localStreamId, false, false, true), 'id')
    }

    case AgoraStopScreenShareAction.type: {
      if (action.meta !== 'success') {
        return prevState
      }
      const { streamId } = action.payload.response
      return removeByKeyFromCollection(prevState, streamId)
    }

    case AgoraPublishLocalStream.type: {
      if (action.meta !== 'success') {
        return prevState
      }
      const { streamId, isVideoMute, isAudioMute } = action.payload.response

      return setItemInCollection(prevState, new CallPeer(streamId, null, isVideoMute, isAudioMute, true), 'id')
    }

    case AgoraUnPublishLocalStream.type: {
      if (action.meta !== 'success') {
        return prevState
      }

      const { streamId } = action.payload.response

      return removeByKeyFromCollection(prevState, streamId)
    }

    case AgoraLeaveChannelAction.type: {
      return createEmptyCollection()
    }

    case AgoraMuteVideoAction.type: {
      if (action.meta !== 'success') {
        return prevState
      }

      const streamId = action.payload.params
      if (!streamId) {
        return prevState
      }

      const prevPeer = getItemFromCollection(prevState, streamId)
      if (!prevPeer) {
        return prevState
      }

      return setItemInCollection(prevState, {
        ...prevPeer,
        isVideoMuted: true,
      }, 'id')
    }

    case AgoraUnmuteVideoAction.type: {
      if (action.meta !== 'success') {
        return prevState
      }

      const streamId = action.payload.params
      if (!streamId) {
        return prevState
      }

      const prevPeer = getItemFromCollection(prevState, streamId)
      if (!prevPeer) {
        return prevState
      }

      return setItemInCollection(prevState, {
        ...prevPeer,
        isVideoMuted: false,
      }, 'id')
    }

    case AgoraMuteAudioAction.type: {
      if (action.meta !== 'success') {
        return prevState
      }

      const streamId = action.payload.params
      if (!streamId) {
        return prevState
      }

      const prevPeer = getItemFromCollection(prevState, streamId)
      if (!prevPeer) {
        return prevState
      }

      return setItemInCollection(prevState, {
        ...prevPeer,
        isAudioMuted: true,
      }, 'id')
    }

    case AgoraUnmuteAudioAction.type: {
      if (action.meta !== 'success') {
        return prevState
      }

      const streamId = action.payload.params
      if (!streamId) {
        return prevState
      }

      const prevPeer = getItemFromCollection(prevState, streamId)
      if (!prevPeer) {
        return prevState
      }

      return setItemInCollection(prevState, {
        ...prevPeer,
        isAudioMuted: false,
      }, 'id')
    }

    case AgoraClientEventAction.type: {
      const event = action.payload
      switch (event.type) {
        case 'peer-online': {
          const id = +event.uid
          const prevPeer = getItemFromCollection(prevState, id)
          if (prevPeer && prevPeer.isLocal) {
            return prevState
          }

          return setItemInCollection(prevState, new CallPeer(+event.uid, null, false, false), 'id')
        }

        case 'peer-leave': {
          return removeByKeyFromCollection(prevState, +event.uid)
        }

        case 'stream-added': {
          const { stream } = event
          const id = +(stream?.getId() || 0)

          const prevPeer = getItemFromCollection(prevState, id) || new CallPeer(id)

          if (prevPeer && prevPeer.isLocal) {
            return prevState
          }
          return setItemInCollection(prevState, {
            ...prevPeer,
            streamId: id,
          }, 'id')
        }

        case 'stream-subscribed': {
          const { stream } = event
          const id = +(stream?.getId() || 0)
          const prevPeer = getItemFromCollection(prevState, id)
          if (!prevPeer) {
            return prevState
          }

          return setItemInCollection(prevState, {
            ...prevPeer,
            streamId: id,
          }, 'id')
        }

        case 'stream-removed': {
          const id = +(event.stream?.getId() || 0)
          const prevPeer = getItemFromCollection(prevState, id)
          if (!prevPeer) {
            return prevState
          }

          return setItemInCollection(prevState, {
            ...prevPeer,
            streamId: null,
            isReconnection: false,
            isAudioMuted: false,
          }, 'id')
        }

        case 'stream-published': {
          const streamId = +(event.stream?.getId() || 0)
          const prevPeer = getItemFromCollection(prevState, streamId) || new CallPeer(streamId, streamId, false, false, true)
          if (!prevPeer) {
            return prevState
          }
          return setItemInCollection(prevState, {
            ...prevPeer,
            streamId,
          }, 'id')
        }

        case 'stream-unpublished': {
          const { stream } = event
          const streamId = +stream.getId()
          return removeByKeyFromCollection(prevState, streamId)
        }

        case 'stream-reconnect-start': {
          const id = +(event.uid || 0)
          const prevPeer = getItemFromCollection(prevState, id)
          if (!prevPeer) {
            return prevState
          }

          return setItemInCollection(prevState, {
            ...prevPeer,
            isReconnection: true,
          }, 'id')
        }

        case 'stream-reconnect-end': {
          const id = +(event.uid || 0)
          const prevPeer = getItemFromCollection(prevState, id)
          if (!prevPeer) {
            return prevState
          }

          return setItemInCollection(prevState, {
            ...prevPeer,
            isReconnection: false,
          }, 'id')
        }

        case 'disable-local-video':
        case 'mute-video': {
          const id = +(event.uid || 0)
          const prevPeer = getItemFromCollection(prevState, id)
          if (!prevPeer) {
            return prevState
          }

          return setItemInCollection(prevState, {
            ...prevPeer,
            isVideoMuted: true,
          }, 'id')
        }

        case 'enable-local-video':
        case 'unmute-video': {
          const id = +(event.uid || 0)
          const prevPeer = getItemFromCollection(prevState, id)
          if (!prevPeer) {
            return prevState
          }

          return setItemInCollection(prevState, {
            ...prevPeer,
            isVideoMuted: false,
          }, 'id')
        }

        case 'mute-audio': {
          const id = +(event.uid || 0)
          const prevPeer = getItemFromCollection(prevState, id)
          if (!prevPeer || prevPeer.isLocal || isScreenShare(id)) {
            return prevState
          }

          return setItemInCollection(prevState, {
            ...prevPeer,
            isAudioMuted: true,
          }, 'id')

        }

        case 'unmute-audio': {
          const id = +(event.uid || 0)
          const prevPeer = getItemFromCollection(prevState, id)
          if (!prevPeer) {
            return prevState
          }

          return setItemInCollection(prevState, {
            ...prevPeer,
            isAudioMuted: false,
          }, 'id')

        }

        case 'connection-state-change': {
          if (event.curState === 'DISCONNECTED') {
            return createEmptyCollection()
          }

          return prevState
        }

      }
    }
  }


  return prevState
}
